import { useEffect, useState } from "react";
import { Mail, Send, Clock, Users2, AlertTriangle, ArrowUpRight, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { adminHttp, formatApiError } from "../../lib/adminApi";
import { toast } from "sonner";
import LogModal from "../components/EmailLogModal";

const TEMPLATE_COLORS = {
  quote_received: "#8cb62c",
  catalog_delivery: "#F28340",
  case_studies_delivery: "#FF9E66",
  preliminary_quote: "#C7521A",
  nurturing_day_1: "#9E3E15",
  nurturing_day_3: "#762C0F",
  nurturing_day_7: "#4F1D0A",
  internal_lead_alert: "#3D5311",
  post_quote_followup: "#8cb62c",
  subscribe_thanks: "#888",
};

export default function EmailsDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openLog, setOpenLog] = useState(null);

  const load = async () => {
    try {
      const { data } = await adminHttp.get("/admin/emails/stats");
      setStats(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resend = async (id) => {
    try {
      await adminHttp.post(`/admin/emails/resend/${id}`);
      toast.success("Email reenviado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !stats) {
    return <div className="p-10 text-[#6B7280] font-mono text-xs uppercase tracking-widest">Cargando…</div>;
  }

  const chartData = Object.entries(stats.by_template || {}).map(([k, v]) => ({
    template: k.replace(/_/g, " "),
    key: k,
    total: v.total || 0,
    sent: v.sent || 0,
    failed: v.failed || 0,
  }));

  return (
    <div className="p-6 lg:p-10" data-testid="emails-dashboard">
      <div className="flex items-center gap-3 mb-2">
        <span className="divider-accent" /><span className="kicker">Email Marketing</span>
      </div>
      <h1 className="font-display font-black tracking-tightest text-4xl md:text-5xl">Dashboard de emails</h1>
      <p className="mt-3 text-white/55 max-w-2xl">Monitoreo de entregas, nurturing activo y desempeño de las plantillas automáticas.</p>

      {/* KPIs */}
      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Send} label="Enviados últimos 7d" value={stats.sent_7d} />
        <KPI icon={Mail} label="Success rate" value={`${stats.success_rate}%`} accent />
        <KPI icon={Clock} label="Jobs pendientes" value={stats.pending_jobs} />
        <KPI icon={Users2} label="Nurturing activo" value={stats.active_nurturing} />
      </div>

      {/* Chart */}
      <div className="mt-10 bg-white border border-[#DDE2D5] p-6">
        <h2 className="font-display font-black text-xl mb-4">Emails por plantilla (últimos 30 días)</h2>
        {chartData.length === 0 ? (
          <p className="text-[#6B7280] text-sm py-10 text-center">Aún no hay datos. Enviaré gráfica al recibir al menos un lead.</p>
        ) : (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid stroke="#EAECEA" vertical={false} />
                <XAxis dataKey="template" tick={{ fill: "#888", fontSize: 10 }} />
                <YAxis tick={{ fill: "#888", fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #8cb62c", color: "#1F2937" }} />
                <Bar dataKey="sent" stackId="a" fill="#8cb62c" />
                <Bar dataKey="failed" stackId="a" fill="#662222" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Last 20 */}
      <div className="mt-10 bg-white border border-[#DDE2D5]">
        <div className="px-6 py-4 border-b border-[#DDE2D5] flex items-center justify-between">
          <h2 className="font-display font-black text-xl">Últimos 20 emails</h2>
          <Link to="/admin/emails/logs" className="font-mono text-[10px] uppercase tracking-widest text-accent hover:underline flex items-center gap-1">
            Ver todos <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F2F4ED] text-[#6B7280] font-mono text-[10px] uppercase tracking-widest">
                <th className="text-left px-6 py-3">Fecha</th>
                <th className="text-left px-6 py-3">Plantilla</th>
                <th className="text-left px-6 py-3">Destinatario</th>
                <th className="text-left px-6 py-3">Status</th>
                <th className="text-left px-6 py-3">Lead</th>
                <th className="text-right px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody data-testid="emails-dashboard-last">
              {(stats.last || []).map((l) => (
                <tr key={l.id} className="border-t border-[#EAECEA] hover:bg-[#F2F4ED]/50">
                  <td className="px-6 py-3 font-mono text-[11px] text-[#374151]">
                    {format(parseISO(l.created_at), "d MMM · HH:mm", { locale: es })}
                  </td>
                  <td className="px-6 py-3">
                    <span
                      className="px-2 py-1 font-mono text-[10px] uppercase"
                      style={{ color: TEMPLATE_COLORS[l.template_key] || "#8cb62c", border: `1px solid ${TEMPLATE_COLORS[l.template_key] || "#8cb62c"}` }}
                    >
                      {l.template_key}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-white/75 truncate max-w-[240px]">{(l.to || []).join(", ")}</td>
                  <td className="px-6 py-3">
                    <StatusBadge status={l.status} />
                  </td>
                  <td className="px-6 py-3">
                    {l.lead_id ? (
                      <Link to={`/admin/leads/${l.lead_id}`} className="text-accent hover:underline font-mono text-[11px]">
                        {l.lead_id.slice(0, 8)}
                      </Link>
                    ) : <span className="text-[#9CA3AF]">—</span>}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setOpenLog(l.id)} className="p-2 text-[#4B5563] hover:text-accent" title="Ver">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={() => resend(l.id)} className="p-2 text-[#4B5563] hover:text-accent" title="Reenviar">
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(stats.last || []).length === 0 && (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#6B7280] font-mono text-xs">Sin emails aún.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openLog && <LogModal logId={openLog} onClose={() => setOpenLog(null)} onResend={() => { resend(openLog); setOpenLog(null); }} />}
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent }) {
  return (
    <div className={`p-6 border ${accent ? "border-accent/60 bg-accent/5" : "border-[#DDE2D5] bg-white"}`}>
      <div className="flex items-center justify-between">
        <Icon className={`w-5 h-5 ${accent ? "text-accent" : "text-[#6B7280]"}`} />
      </div>
      <p className="mt-4 font-display font-black text-4xl tabular-nums tracking-tight">{value ?? "—"}</p>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">{label}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    sent: { bg: "#1e4d29", text: "#7fff9f", label: "Enviado" },
    failed: { bg: "#4d1e1e", text: "#ff7f7f", label: "Fallido" },
    queued: { bg: "#3D5311", text: "#8a8aff", label: "En cola" },
    skipped: { bg: "#2e2e2e", text: "#aaa", label: "Omitido" },
    running: { bg: "#4d3f1e", text: "#ffd97f", label: "Ejecutando" },
    cancelled: { bg: "#2e2e2e", text: "#888", label: "Cancelado" },
    pending: { bg: "#3D5311", text: "#8a8aff", label: "Pendiente" },
  };
  const c = map[status] || { bg: "#2e2e2e", text: "#aaa", label: status };
  return (
    <span style={{ background: c.bg, color: c.text }}
          className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider">{c.label}</span>
  );
}
